// API wrappers + helpers for the restaurant menu module's CMS pages
// (Menus / Items tabs + the per-menu editor). Not to be confused with
// menus.ts (site navigation). Item photos come from the Media library via
// the shared ImageField picker.
import type { ApiClient } from "./api";

export interface MenuItemVariant {
  id?: string;
  name: string;
  price_pence: number | null;
}

export interface MenuItem {
  id: string;
  name: string;
  description: string | null;
  price_pence: number | null;
  allergens: string[];
  dietary_tags: string[];
  image_r2_key: string | null;
  active: boolean;
  variants: MenuItemVariant[];
  /** Library view only: names of the menus this item appears on. */
  menu_names?: string[];
}

export interface MenuSection {
  id: string;
  name: string;
  description: string | null;
  items: MenuItem[];
}

export interface Menu {
  id: string;
  name: string;
  description: string | null;
  active: boolean;
  sections: MenuSection[];
}

export interface MenuModel {
  menus: Menu[];
  items: MenuItem[];
  assetsBaseUrl: string | null;
  allergens: string[];
  dietaryTags: string[];
}

// Human labels for the 14 mandatory UK allergens (slugs match the API's
// UK_ALLERGENS) and the dietary tags.
export const ALLERGEN_LABELS: Record<string, string> = {
  celery: "Celery",
  cereals_containing_gluten: "Cereals containing gluten",
  crustaceans: "Crustaceans",
  eggs: "Eggs",
  fish: "Fish",
  lupin: "Lupin",
  milk: "Milk",
  molluscs: "Molluscs",
  mustard: "Mustard",
  peanuts: "Peanuts",
  sesame: "Sesame",
  soybeans: "Soybeans",
  sulphur_dioxide: "Sulphur dioxide / sulphites",
  tree_nuts: "Tree nuts",
};

export const DIETARY_LABELS: Record<string, string> = {
  vegetarian: "Vegetarian",
  vegan: "Vegan",
  gluten_free: "Gluten-free",
  dairy_free: "Dairy-free",
  halal: "Halal",
  spicy: "Spicy",
};

export function tagLabel(slug: string, labels: Record<string, string>): string {
  return labels[slug] ?? slug;
}

/** Pence → "£12.50" (whole pounds shown without decimals). */
export function formatPrice(pence: number | null | undefined): string {
  if (typeof pence !== "number") return "—";
  const pounds = pence / 100;
  return `£${pence % 100 === 0 ? pounds.toFixed(0) : pounds.toFixed(2)}`;
}

/** One line summarising what an item costs, variants included. */
export function priceSummary(item: Pick<MenuItem, "price_pence" | "variants">): string {
  if (item.variants.length > 0) {
    return item.variants
      .map((variant) => `${variant.name} ${variant.price_pence != null ? formatPrice(variant.price_pence) : formatPrice(item.price_pence)}`)
      .join(" · ");
  }
  return formatPrice(item.price_pence);
}

/** Pence → the editable "12.50" input value. */
export function priceToInput(pence: number | null | undefined): string {
  if (typeof pence !== "number") return "";
  return (pence / 100).toFixed(2);
}

/**
 * "12.50" → 1250, integer-safe (never float maths on pounds). Returns null
 * for an empty input (no price) and false for anything unparseable.
 */
export function parsePriceInput(value: string): number | null | false {
  const trimmed = value.trim().replace(/^£/, "");
  if (!trimmed) return null;
  const match = /^(\d+)(?:\.(\d{1,2}))?$/.exec(trimmed);
  if (!match) return false;
  const pounds = Number(match[1]);
  const pence = Number((match[2] ?? "").padEnd(2, "0") || "0");
  return pounds * 100 + pence;
}

export async function fetchMenuModel(api: ApiClient): Promise<MenuModel> {
  const data = await api.request<{
    menus?: Menu[];
    items?: MenuItem[];
    assets_base_url?: string | null;
    allergens?: string[];
    dietary_tags?: string[];
  }>("/tenant/menu");
  return {
    menus: data.menus ?? [],
    items: data.items ?? [],
    assetsBaseUrl: data.assets_base_url ?? null,
    allergens: data.allergens ?? Object.keys(ALLERGEN_LABELS),
    dietaryTags: data.dietary_tags ?? Object.keys(DIETARY_LABELS),
  };
}

// --- Menus ---

export function createMenu(
  api: ApiClient,
  fields: { name: string; description?: string | null }
): Promise<{ id: string }> {
  return api.request("/tenant/menu/menus", {
    method: "POST",
    body: JSON.stringify(fields),
  });
}

export function updateMenu(
  api: ApiClient,
  id: string,
  fields: Partial<{ name: string; description: string | null; active: boolean }>
): Promise<{ id: string }> {
  return api.request(`/tenant/menu/menus/${encodeURIComponent(id)}`, {
    method: "PUT",
    body: JSON.stringify(fields),
  });
}

export function deleteMenu(api: ApiClient, id: string): Promise<unknown> {
  return api.request(`/tenant/menu/menus/${encodeURIComponent(id)}`, {
    method: "DELETE",
  });
}

export function reorderMenus(api: ApiClient, ids: string[]): Promise<unknown> {
  return api.request("/tenant/menu/menus/order", {
    method: "PUT",
    body: JSON.stringify({ ids }),
  });
}

// --- Sections ---

export function createSection(
  api: ApiClient,
  menuId: string,
  fields: { name: string; description?: string | null }
): Promise<{ id: string }> {
  return api.request("/tenant/menu/sections", {
    method: "POST",
    body: JSON.stringify({ menu_id: menuId, ...fields }),
  });
}

export function updateSection(
  api: ApiClient,
  id: string,
  fields: Partial<{ name: string; description: string | null }>
): Promise<{ id: string }> {
  return api.request(`/tenant/menu/sections/${encodeURIComponent(id)}`, {
    method: "PUT",
    body: JSON.stringify(fields),
  });
}

export function deleteSection(api: ApiClient, id: string): Promise<unknown> {
  return api.request(`/tenant/menu/sections/${encodeURIComponent(id)}`, {
    method: "DELETE",
  });
}

export function reorderSections(
  api: ApiClient,
  menuId: string,
  ids: string[]
): Promise<unknown> {
  return api.request("/tenant/menu/sections/order", {
    method: "PUT",
    body: JSON.stringify({ menu_id: menuId, ids }),
  });
}

// --- Items (library) ---

export interface ItemFields {
  name: string;
  description: string | null;
  price_pence: number | null;
  allergens: string[];
  dietary_tags: string[];
  image_r2_key: string | null;
  variants: { name: string; price_pence: number | null }[];
}

/** Create a library item; sectionId also attaches it there (inline add). */
export function createItem(
  api: ApiClient,
  fields: ItemFields,
  sectionId?: string
): Promise<{ id: string }> {
  return api.request("/tenant/menu/items", {
    method: "POST",
    body: JSON.stringify(sectionId ? { ...fields, section_id: sectionId } : fields),
  });
}

export function updateItem(
  api: ApiClient,
  id: string,
  fields: Partial<ItemFields & { active: boolean }>
): Promise<{ id: string }> {
  return api.request(`/tenant/menu/items/${encodeURIComponent(id)}`, {
    method: "PUT",
    body: JSON.stringify(fields),
  });
}

export function deleteItem(api: ApiClient, id: string): Promise<unknown> {
  return api.request(`/tenant/menu/items/${encodeURIComponent(id)}`, {
    method: "DELETE",
  });
}

export function attachItem(
  api: ApiClient,
  sectionId: string,
  itemId: string
): Promise<unknown> {
  return api.request(`/tenant/menu/sections/${encodeURIComponent(sectionId)}/items`, {
    method: "POST",
    body: JSON.stringify({ item_id: itemId }),
  });
}

export function detachItem(
  api: ApiClient,
  sectionId: string,
  itemId: string
): Promise<unknown> {
  return api.request(`/tenant/menu/sections/${encodeURIComponent(sectionId)}/items/${encodeURIComponent(itemId)}`,
    { method: "DELETE" }
  );
}

export function reorderSectionItems(
  api: ApiClient,
  sectionId: string,
  ids: string[]
): Promise<unknown> {
  return api.request(`/tenant/menu/sections/${encodeURIComponent(sectionId)}/items/order`,
    { method: "PUT", body: JSON.stringify({ ids }) }
  );
}

// --- QR ---

/** The table QR code: an SVG pointing at the live site's own /menu page. */
export function fetchMenuQr(
  api: ApiClient
): Promise<{ svg: string; url: string }> {
  return api.request("/tenant/menu/qr");
}
